import {
  Button, Checkbox, FormControlLabel, ListItem,
} from '@mui/material';
import classNames from 'classnames';
import * as React from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { AppConstantsContext } from '../../../../../context';
import type { InstrumentType } from '../../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { JobType } from '../../../../../entities';
import { useNextStepAfterUpdate } from '../Wizard/Wizard.hooks';
import { INSTRUMENTS } from './Instrument.config';
import InstrumentsDetectionWrapper from './InstrumentsDetectionWrapper';

const useStyles = makeStyles()((theme) => ({
  instrumentSelection: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    alignItems: 'center',
    padding: 7,
    '&:hover': {
      boxShadow: `0px 0px 5px 0 ${theme.palette.secondary.main}`,
    },
    '&:disabled': {
      filter: 'grayscale(.8) opacity(0.8)',
    },
  },
  instruments: {
    gap: 1,
    margin: 1,
    marginLeft: 5,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      gap: 2,
    },
  },
  models: {
    alignItems: 'start !important',
    justifyContent: 'start !important',
  },
  label: {
    height: '3.4em',
    width: '5.3em',
    color: '#d1cfcf',
    fontSize: '12px',
    textTransform: 'initial',
  },
  selected: {
    boxShadow: `0px 0px 5px 0 ${theme.palette.primary.main}`,
  },
  checkbox: {
    // width: '100%',
    textAlign: 'left',
    fontSize: '12px',
    padding: 0,
    paddingLeft: '7',
    // textAlign: 'center',
  },
  classicGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    padding: '0 0 0 25px',
    gap: 0,
    '& > li': {
      fontSize: '12px',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      justifyItems: 'center',
    },
  },
  gridLabel: {
    fontSize: '12px',
  },
  buttons: {
    textAlign: 'right',
  },
}));

export interface InstrumentsSelectionProps {
  isRetranscribe?: boolean;
}

const InstrumentsSelection: React.FC<InstrumentsSelectionProps> = ({ isRetranscribe }) => {
  const { classes } = useStyles();
  const { jobType } = useContext(AppConstantsContext);
  const { t } = useTranslation();
  const [instruments, setInstruments] = React.useState<InstrumentType[]>([]);
  const [showNextButton, setShowNextButton] = React.useState(false);
  const [disableInstruments, setDisableInstruments] = React.useState(false);
  const [model, setModel] = React.useState<'universal' | 'rock' | 'classic'>('universal');
  const next = useNextStepAfterUpdate();

  const handleClick = (type: InstrumentType | 'multiple') => {
    if (type === 'multiple') {
      window.open('https://klang.io/transcription-studio', '_blank');
      return;
    }
    if (instruments.includes(type)) {
      // remove instrument
      const filteredInstruments = instruments.filter((instrument) => instrument !== type);
      setShowNextButton(filteredInstruments.length > 0);
      setInstruments(filteredInstruments);
    } else if (jobType === JobType.TranscribeStudio) {
      // add instrument to multiple
      setInstruments([...instruments, type]);
      setShowNextButton(true);
    } else {
      // set single instrument
      next({ instruments: [type] });
    }
  };

  const handleClickNext = () => {
    next({ instruments, model });
  };

  const handleDetectionChange = (detection: boolean) => {
    if (detection) {
      setDisableInstruments(true);
      setInstruments([]);
      setShowNextButton(true);
    } else {
      setDisableInstruments(false);
      setShowNextButton(false);
    }
  };

  return (
    <>
      <InstrumentsDetectionWrapper
        onDetectionChange={handleDetectionChange}
        showModels={window.location?.search?.includes('test')}
        model={model}
        onModelChange={setModel}
      >
        {model === 'classic' ? (
          <div className={classes.classicGrid}>
            {INSTRUMENTS?.[jobType]?.config
              .filter((iconConfig) => iconConfig?.models?.includes(model))
              .map((iconConfig) => (
                <ListItem key={`classic-${iconConfig.type}`} className={classes.checkbox}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        name={iconConfig.type}
                        onClick={() => handleClick(iconConfig.type)}
                        id={`btn_wizard_${iconConfig.type}`}
                        checked={instruments.includes(iconConfig.type as InstrumentType)}
                      />
                    )}
                    label={<div className={classes.gridLabel}>{t(iconConfig.label)}</div>}
                  />
                </ListItem>
              ))}
          </div>
        ) : (
          <div
            className={classNames(classes.instruments, {
              [classes.models]: jobType === JobType.TranscribeStudio,
            })}
          >
            {INSTRUMENTS?.[jobType]?.config
              .filter(
                (iconConfig) =>
                  jobType !== JobType.TranscribeStudio || iconConfig?.models?.includes(model),
              )
              .map((iconConfig) => (
                <div key={iconConfig.type}>
                  {!(iconConfig.type === 'multiple' && isRetranscribe) && (
                    <Button
                      name={iconConfig.type}
                      disabled={disableInstruments}
                      id={`btn_wizard_${iconConfig.type}`}
                      className={
                        instruments.includes(iconConfig.type as InstrumentType)
                          ? `${classes.instrumentSelection} ${classes.selected}`
                          : classes.instrumentSelection
                      }
                      key={iconConfig.type}
                      onClick={() => handleClick(iconConfig.type)}
                    >
                      <div>{iconConfig.component}</div>
                      <div className={classes.label}>{t(iconConfig.label)}</div>
                    </Button>
                  )}
                </div>
              ))}
          </div>
        )}
      </InstrumentsDetectionWrapper>
      {showNextButton && jobType === JobType.TranscribeStudio && (
        <div className={classes.buttons}>
          <Button
            id="btn_wizard_choose_instrument_next"
            onClick={handleClickNext}
            variant="contained"
          >
            {t('home.input.dialog.next')}
          </Button>
        </div>
      )}
    </>
  );
};

export default InstrumentsSelection;
